import React from "react";
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import Svg, { Defs, LinearGradient, Rect, Stop } from "react-native-svg";
import LogoSquare from "../../../assets/logoSquare";
import { SignUpViewProps } from "../auth.types";
import PasswordInput from "./PasswordInput";

const BackgroundGradient = () => (
  <Svg style={StyleSheet.absoluteFillObject} width="100%" height="100%">
    <Defs>
      <LinearGradient id="signUpMobileBg" x1="0" y1="0" x2="0" y2="1">
        <Stop offset="0%" stopColor="#FCF2DC" />
        <Stop offset="100%" stopColor="#F3FEFF" />
      </LinearGradient>
    </Defs>
    <Rect width="100%" height="100%" fill="url(#signUpMobileBg)" />
  </Svg>
);

export default function SignUpViewMobile({
  name,
  email,
  password,
  confirmPassword,
  isLoading,
  isSaving,
  error,
  canSignUp,
  onNameChange,
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onPressJoin,
  onSwitchToSignIn,
}: SignUpViewProps) {
  if (isLoading) {
    return (
      <View style={styles.root}>
        <BackgroundGradient />
        <View style={styles.loadingCard}>
          <LogoSquare width={76} height={94} />
          <ActivityIndicator style={styles.loadingSpinner} />
          <Text style={styles.loadingText}>Loading...</Text>
        </View>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.root}
      behavior={Platform.OS === "ios" ? "padding" : "height"}
    >
      <BackgroundGradient />
      <KeyboardAvoidingView
        style={styles.keyboardAvoider}
        behavior={Platform.OS === "ios" ? "padding" : undefined}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.card}>
            <View style={styles.cardContent}>
              <View style={styles.logoWrap}>
                <LogoSquare width={91} height={113} />
              </View>
              <Text style={styles.title}>Create your KNEXO account</Text>
              <Text style={styles.subtitle}>
                Join guided, respectful, time-limited conversations designed to
                feel comfortable.
              </Text>

              <TextInput
                style={[styles.input, styles.inputAccent]}
                placeholder="Name"
                placeholderTextColor="#9AA0A6"
                autoCapitalize="words"
                value={name}
                onChangeText={onNameChange}
                returnKeyType="next"
              />

              <TextInput
                style={styles.input}
                placeholder="Email"
                placeholderTextColor="#9AA0A6"
                autoCapitalize="none"
                keyboardType="email-address"
                value={email}
                onChangeText={onEmailChange}
                returnKeyType="next"
              />

              <PasswordInput
                style={styles.input}
                placeholder="Password"
                placeholderTextColor="#9AA0A6"
                iconColor="#9AA0A6"
                value={password}
                onChangeText={onPasswordChange}
                returnKeyType="next"
              />

              <PasswordInput
                style={styles.input}
                placeholder="Confirm password"
                placeholderTextColor="#9AA0A6"
                iconColor="#9AA0A6"
                value={confirmPassword}
                onChangeText={onConfirmPasswordChange}
                returnKeyType="done"
                onSubmitEditing={onPressJoin}
              />

              {error ? <Text style={styles.errorText}>{error}</Text> : null}

              <TouchableOpacity
                onPress={onPressJoin}
                disabled={!canSignUp || isSaving}
                style={[
                  styles.primaryButton,
                  (!canSignUp || isSaving) && styles.primaryButtonDisabled,
                ]}
              >
                {isSaving ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text style={styles.primaryButtonText}>Sign Up</Text>
                )}
              </TouchableOpacity>

              <TouchableOpacity
                onPress={onSwitchToSignIn}
                disabled={!onSwitchToSignIn}
                style={styles.switchButton}
              >
                <Text style={styles.switchText}>
                  Already have an account?{" "}
                  <Text style={styles.switchLink}>Sign In</Text>
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: "#F6F7F9",
  },
  keyboardAvoider: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: "center",
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  card: {
    width: "100%",
    maxWidth: 343,
    alignSelf: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    paddingHorizontal: 20,
    paddingVertical: 28,
    shadowColor: "#000",
    shadowOffset: { width: 8, height: 8 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 4,
  },
  cardContent: {
    alignItems: "center",
    gap: 18,
  },
  logoWrap: {
    alignItems: "center",
  },
  title: {
    fontSize: 24,
    lineHeight: 28,
    fontWeight: "700",
    color: "#2B2B2F",
    textAlign: "center",
  },
  subtitle: {
    fontSize: 16,
    lineHeight: 22,
    color: "#2B2B2F",
    textAlign: "center",
  },
  input: {
    height: 60,
    borderWidth: 1,
    borderColor: "#D8DBE1",
    borderRadius: 16,
    paddingHorizontal: 20,
    fontSize: 16,
    color: "#2B2B2F",
    backgroundColor: "#FFFFFF",
    width: "100%",
  },
  inputAccent: {
    borderColor: "#7EADEB",
  },
  errorText: {
    color: "#D32F2F",
    fontSize: 12,
    textAlign: "center",
  },
  primaryButton: {
    width: "100%",
    height: 56,
    borderRadius: 16,
    backgroundColor: "#3A6FB1",
    alignItems: "center",
    justifyContent: "center",
  },
  primaryButtonDisabled: {
    opacity: 0.6,
  },
  primaryButtonText: {
    fontSize: 14,
    fontWeight: "400",
    color: "#FFFFFF",
  },
  switchButton: {
    alignItems: "center",
  },
  switchText: {
    fontSize: 14,
    lineHeight: 20,
    color: "#2B2B2F",
    textAlign: "center",
  },
  switchLink: {
    color: "#3A6FB1",
    fontWeight: "700",
  },
  loadingCard: {
    marginHorizontal: 16,
    alignSelf: "center",
    borderRadius: 16,
    backgroundColor: "#FFFFFF",
    paddingHorizontal: 24,
    paddingVertical: 32,
    alignItems: "center",
    gap: 12,
    shadowColor: "#000",
    shadowOffset: { width: 8, height: 8 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 4,
  },
  loadingSpinner: {
    marginTop: 12,
  },
  loadingText: {
    fontSize: 14,
    color: "#2B2B2F",
  },
});
